import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

// Shared RoboAdmin primitives. Extracted so the PDF pipeline and the legacy
// image flow render the same controls rather than drifting into two dark themes.

export const pluralise = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const Select = ({ label, value, onChange, options, disabled }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium uppercase tracking-wider text-white/50">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white
                 outline-none focus:border-[#2e92b0] focus:bg-white/10 disabled:opacity-40
                 disabled:cursor-not-allowed transition-colors"
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#133042]">{o}</option>
      ))}
    </select>
  </div>
);

export const StatusPill = ({ color, children }) => {
  const colours = {
    green:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber:  "bg-amber-500/15  text-amber-300  border-amber-500/30",
    blue:   "bg-sky-500/15    text-sky-300    border-sky-500/30",
    slate:  "bg-white/10      text-white/60   border-white/15",
    red:    "bg-red-500/15    text-red-300    border-red-500/30",
    violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    rose:   "bg-rose-500/15   text-rose-300   border-rose-500/35",
  };
  return (
    // Labels run as long as "Extraction unavailable", so the pill is sized for the
    // worst case: smaller text, more room around it, and never wrapping mid-phrase.
    <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-medium leading-none ${colours[color] ?? colours.slate}`}>
      {children}
    </span>
  );
};

export const Button = ({ variant = "secondary", size = "md", className = "", ...props }) => {
  const styles = {
    primary:   "bg-[#2e92b0] text-white hover:bg-[#257a94] disabled:bg-white/10 disabled:text-white/30",
    // Approval is the one irreversible-feeling action on a card, so it gets its own
    // colour rather than sharing primary with every other blue button on screen.
    success:   "bg-emerald-500/90 text-white hover:bg-emerald-500 disabled:bg-white/10 disabled:text-white/30",
    secondary: "border border-white/15 bg-white/5 text-white/80 hover:bg-white/10 disabled:opacity-30",
    danger:    "border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-30",
    ghost:     "text-white/60 hover:text-white hover:bg-white/10",
  };
  const sizes = {
    md: "rounded-lg px-3.5 py-2 text-sm",
    sm: "rounded-md px-2.5 py-1.5 text-xs",
    // For the asset cards, which are a third of the grid wide and carry four actions.
    xs: "rounded-md px-2 py-1 text-[11px]",
  };
  return (
    <button
      type="button"
      // nowrap so a flex-1 button shrinks rather than breaking its label onto two
      // lines, which would make one button taller than the rest of the row.
      className={`whitespace-nowrap font-medium transition-colors disabled:cursor-not-allowed ${sizes[size]} ${styles[variant]} ${className}`}
      {...props}
    />
  );
};

// One number and what it counts. The paper dashboard is mostly made of these.
export const Stat = ({ label, value, tone = "slate", hint }) => {
  const tones = {
    slate:   "text-white",
    green:   "text-emerald-300",
    amber:   "text-amber-300",
    red:     "text-red-300",
    blue:    "text-sky-300",
  };
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
      <p className={`text-xl font-semibold tabular-nums ${tones[tone] ?? tones.slate}`}>{value}</p>
      <p className="mt-0.5 text-[11px] leading-tight text-white/50">{label}</p>
      {hint && <p className="mt-1 text-[10px] leading-tight text-white/35">{hint}</p>}
    </div>
  );
};

// `collapsible` adds a disclosure toggle to the header. `defaultOpen` is only the
// STARTING state, so a section can open itself once it has something worth reading
// (images once they exist, flags once one is serious) while still letting the reader
// close it again — a section that re-opened itself on every poll would be unusable.
export const Panel = ({ title, action, children, subtitle, collapsible = false, defaultOpen = true, highlight = false, autoOpen }) => {
  const [open, setOpen] = useState(defaultOpen);

  // `autoOpen` follows the pipeline: open the section being worked on, close the ones
  // that are finished. It re-syncs only when the value CHANGES, never on every render
  // — the paper is re-polled every few seconds while a stage runs, and a panel that
  // recomputed its state each time would shut itself while being read. Between phase
  // changes a manual toggle stands.
  const previous = useRef(autoOpen);
  useEffect(() => {
    if (autoOpen === undefined || autoOpen === previous.current) return;
    previous.current = autoOpen;
    setOpen(autoOpen);
  }, [autoOpen]);

  const isOpen = collapsible ? open : true;

  const heading = (
    <div className="flex min-w-0 items-center gap-2">
      {collapsible && (
        <span
          aria-hidden="true"
          className={`text-white/40 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
        >
          ›
        </span>
      )}
      <div className="min-w-0 text-left">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-white/45">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <section className={`rounded-xl border transition-colors ${
      // The section you are meant to be working in. A steady ring rather than a blink:
      // this can stay on screen for the length of a review, and something flashing
      // that whole time is exhausting rather than helpful.
      highlight
        ? "border-[#2e92b0]/50 bg-[#2e92b0]/[0.05] ring-1 ring-[#2e92b0]/25"
        : "border-white/10 bg-white/[0.03]"
    }`}>
      <header className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${isOpen ? "border-b border-white/10" : ""}`}>
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={isOpen}
            className="-m-1 flex min-w-0 flex-1 items-center rounded-lg p-1 transition-colors hover:bg-white/[0.04]"
          >
            {heading}
          </button>
        ) : heading}
        <div className="flex shrink-0 items-center gap-2">
          {highlight && (
            <span className="rounded-full bg-[#2e92b0] px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-white">
              You Are Here
            </span>
          )}
          {action}
        </div>
      </header>
      {isOpen && <div className="p-4">{children}</div>}
    </section>
  );
};

// Severity is the whole point of a flag, so it is always the first thing shown.
export const FlagRow = ({ flag }) => {
  const tone = { critical: "red", warning: "amber", info: "slate" }[flag.severity] || "slate";
  return (
    <li className="flex items-start gap-2.5 py-1.5">
      <StatusPill color={tone}>{flag.severity}</StatusPill>
      <div className="min-w-0 text-xs">
        <p className="text-white/80">
          {flag.question && <span className="font-semibold text-white">Q{flag.question} · </span>}
          {flag.message}
        </p>
        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-white/30">
          {flag.type} · {flag.stage}
        </p>
      </div>
    </li>
  );
};

// Keyframes for the pipeline's running state. Rendered once, near the top of the
// pipeline view — Tailwind has no built-in sweep, and a running stage that only
// pulses is hard to tell apart from one that is merely selected.
export const RoboAnimations = () => (
  <style>{`
    @keyframes robo-sweep {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(200%); }
    }
    @keyframes robo-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(46,146,176,0.45); }
      50%      { box-shadow: 0 0 0 6px rgba(46,146,176,0); }
    }
    .robo-sweep::after {
      content: "";
      position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
      animation: robo-sweep 1.6s ease-in-out infinite;
    }
    .robo-glow { animation: robo-glow 2s ease-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .robo-sweep::after, .robo-glow { animation: none; }
    }
  `}</style>
);

// A short prompt that reveals something longer on hover. Used where a card cannot
// afford the space but the full content still has to be reachable — an asset's
// description, or the list of questions depending on it.
export const HoverReveal = ({ label, children, width = "w-64", align = "left" }) => (
  <span className="group relative inline-block">
    <span className="cursor-pointer text-[11px] font-medium leading-relaxed text-white/40 decoration-white/25 decoration-dotted underline-offset-2 transition-colors group-hover:text-[#7fd4ec] group-hover:underline">
      {label}
    </span>
    <span
      role="tooltip"
      className={`pointer-events-none absolute ${align === "right" ? "right-0" : "left-0"} top-full z-30 mt-1.5 ${width} rounded-lg border border-white/15
                  bg-[#0b1c27] px-3 py-2.5 text-[11px] leading-relaxed text-white/75 shadow-xl
                  opacity-0 invisible translate-y-1 transition-all duration-150
                  group-hover:visible group-hover:translate-y-0 group-hover:opacity-100`}
    >
      {children}
    </span>
  </span>
);

// An <img> the API will actually serve.
//
// Auth is a Bearer token on the Authorization header, added by an axios interceptor.
// A plain <img src> is a browser request that carries no such header, so every
// preview came back 401 and rendered as a broken icon. Fetching the bytes through
// axios keeps the token, and an object URL puts them on the page.
// `version` exists because the URL of a crop never changes when the crop does: an
// asset re-cropped from corrected coordinates lives at the same path, so a fetch
// keyed on src alone would keep showing the old picture until a full page reload.
// Pass something that moves when the image does.
export const AuthImage = ({ src, version, alt, className, emptyLabel = "Could not load image" }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) return undefined;

    let cancelled = false;
    let created;
    setFailed(false);
    setObjectUrl(null);

    axios
      .get(src, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        created = URL.createObjectURL(data);
        setObjectUrl(created);
      })
      .catch(() => { if (!cancelled) setFailed(true); });

    // Revoking matters here: a paper can hold dozens of crops, and an object URL
    // holds its blob in memory until it is released.
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src, version]);

  if (failed) {
    return (
      <div className="flex h-32 items-center justify-center px-3 text-center text-xs text-red-300/80">
        {emptyLabel}
      </div>
    );
  }
  if (!objectUrl) {
    return (
      <div className="flex h-32 items-center justify-center px-3 text-center text-xs text-white/30">
        Loading…
      </div>
    );
  }
  return <img src={objectUrl} alt={alt} className={className} />;
};

// A checkbox that belongs to this UI. The native control renders as a light-mode box
// with an OS accent colour, which is why `accent-` never looked right against the
// dark glass — this keeps the same border, radius and teal the buttons and pills use.
//
// The input stays in the DOM as a real checkbox (screen readers, keyboard, focus ring
// via peer-focus-visible); only its appearance is replaced.
export const Checkbox = ({ checked, indeterminate = false, onChange, label, title }) => (
  <label
    title={title}
    className="group inline-flex cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/[0.06]"
  >
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => { if (el) el.indeterminate = indeterminate && !checked; }}
      onChange={onChange}
      className="peer sr-only"
    />
    <span
      aria-hidden="true"
      className="relative flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[5px]
                 border border-white/25 bg-white/[0.06] transition-all
                 group-hover:border-white/45
                 peer-checked:border-[#2e92b0] peer-checked:bg-[#2e92b0]
                 peer-focus-visible:ring-2 peer-focus-visible:ring-[#2e92b0]/50 peer-focus-visible:ring-offset-0"
    >
      {/* Tick for checked, dash for partial. Both are always present and revealed by
          state, because a peer selector cannot swap which element renders. */}
      <svg viewBox="0 0 16 16" className="h-3 w-3 opacity-0 transition-opacity peer-checked:opacity-100">
        <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="white" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {indeterminate && !checked && (
        <span className="absolute h-[2px] w-[9px] rounded-full bg-[#2e92b0]" />
      )}
    </span>
    {label && (
      <span className="text-xs font-medium text-white/60 transition-colors group-hover:text-white/85">
        {label}
      </span>
    )}
  </label>
);
